import React, { useCallback, useEffect, useState } from "react";
import { FiPrinter } from "react-icons/fi";
import { Form, notification } from "antd";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import printJS from "print-js";
import api from "../../../../service";
import generateHeaders from "../../../../utils/generateHeaders";
import { base64ToArrayBuffer, mergePdfs } from "../../../../utils/file";
import { DotIcon } from "../../ServicePanel/card-icons";
import ConfirmationModalContent from "../ConfirmationModalContent";
import {
  ButtonFormConfirm,
  CredentialsListContainer,
  CredentialsListLabel,
  FooterContainer,
  HeaderModal,
  ModalSubTitle,
  ModalTitle,
  ModalWithdrawalAndDisassociate,
  StyledInput,
} from "./styles";
import back from "../../components/Icons/back.svg";
import { StyledFormItem } from "../../../../components/ModalNewCollaborator/styles";

const PrintCredential = ({
  defaulter,
  fetchData,
  open,
  setOpen,
  selectedRowKeys,
  setSelectedRows,
  companyNameCredential,
  isFirstPrint = false,
  onPrintedNow,
}) => {
  const [isDefaulter, setIsDefaulter] = useState(defaulter);
  const [currentStep, setCurrentStep] = useState(1);
  const intl = useIntl();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { token } = useSelector((state) => state.auth);
  const { TextArea } = StyledInput;

  useEffect(() => {
    form.setFieldValue("companyNameCredential", companyNameCredential);
  }, []);

  const reset = useCallback(() => {
    setLoading(false);
    fetchData();
    setCurrentStep(1);
    setSelectedRows([]);
    setIsDefaulter(defaulter);
  }, [
    defaulter,
    setSelectedRows,
    fetchData
  ]);

  const onFinishPrint = useCallback(async () => {
    try {
      const options = generateHeaders(token);
      setLoading(true);

      const credentialIds = selectedRowKeys.map((item) => item.id);
      const values = await form.validateFields();

      if (values?.reason) {
        await api.post(
          "/admin/accreditation/print-override",
          {
            credentialIds,
            reason: values.reason,
          },
          options
        );
      }

      const sanitizedValues = JSON.stringify({
        credentialCompanyName: values.companyNameCredential,
        credentialsId: credentialIds,
        channel: "CAEX",
      });

      const response = await api.patch(
        "/credential-reports",
        sanitizedValues,
        options
      );

      setOpen(false);
      form.resetFields();

      const credentialReports = response.data?.reportOutputList || [];

      if (credentialReports.length > 0) {
        let arrayBuffers = credentialReports.map((apiResult) => {
          return base64ToArrayBuffer(apiResult.file);
        });

        const base64String = btoa(
          [].reduce.call(
            new Uint8Array(await mergePdfs(arrayBuffers)),
            function (p, c) {
              return p + String.fromCharCode(c);
            },
            ""
          )
        );

        printJS({
          printable: base64String,
          type: "pdf",
          base64: true,
        });
      }

      if (onPrintedNow) {
        onPrintedNow(credentialIds);
      }

      reset();
    } catch (error) {
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "api.error." + (error.response?.data?.code || "unknown"),
        }),
      });
      setLoading(false);
    }
  }, [
    form,
    reset,
    selectedRowKeys,
    setOpen,
    token,
    onPrintedNow,
  ]);

  const handleCloseModal = () => {
    reset();
    setOpen(false);
  };

  const handleDefaulterConfirmation = () => {
    setIsDefaulter(false);
  };

  return (
    <ModalWithdrawalAndDisassociate
      width={658}
      height={"auto"}
      closable={false}
      onCancel={handleCloseModal}
      visible={open}
      footer={""}
      destroyOnClose
      centered={true}
    >
      {!isDefaulter && (
        <div>
          <HeaderModal>
            <div className="icon">
              <FiPrinter
                style={{ fontSize: "18px", color: "rgba(6, 142, 195, 1)" }}
              />
            </div>
            <ModalTitle>
              {isFirstPrint
                ? intl.formatMessage({ id: "print_credential_title" })
                : intl.formatMessage({ id: "reprint_credential" })}
            </ModalTitle>
          </HeaderModal>
          {currentStep === 1 ? (
            <>
              <ModalSubTitle>
                {isFirstPrint
                  ? intl.formatMessage({
                      id: "confirm_print_of_credentials"
                    })
                  : intl.formatMessage({
                      id: "confirm_reprint_of_credentials",
                    })}
              </ModalSubTitle>
              <CredentialsListContainer>
                {selectedRowKeys.map((row) => (
                  <CredentialsListLabel key={row.id}>
                    <DotIcon color="#D9D9D9" size={7} />
                    {row.collaboratorName}
                  </CredentialsListLabel>
                ))}
              </CredentialsListContainer>
            </>
          ) : (
            <Form
              form={form}
              onFinish={onFinishPrint}
              layout="vertical"
            >
              <StyledFormItem
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: "error.field_required" }),
                  },
                ]}
                label={intl.formatMessage({ id: "company_name_credential" })}
                name="companyNameCredential"
              >
                <StyledInput
                  placeholder={intl.formatMessage({
                    id: "company_name_credential",
                  })}
                />
              </StyledFormItem>
              {defaulter && (
                <StyledFormItem
                  label={intl.formatMessage({ id: "provide_reason" })}
                  name={"reason"}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "error.field_required",
                      }),
                    },
                  ]}
                >
                  <TextArea />
                </StyledFormItem>
              )}
            </Form>
          )}
          <FooterContainer>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {currentStep > 1 && (
                <img
                  onClick={() => setCurrentStep(currentStep - 1)}
                  src={back}
                  alt="back"
                  style={{ cursor: "pointer" }}
                />
              )}
              <span className="steps">{`${currentStep} ${intl.formatMessage({
                id: "of",
              })} 2`}</span>
            </div>

            <div style={{ marginRight: "10px" }}>
              <ButtonFormConfirm
                onClick={() => {
                  reset();
                  setOpen(false);
                }}
                style={{ border: "none" }}
              >
                {intl.formatMessage({ id: "cancel" })}
              </ButtonFormConfirm>
              <ButtonFormConfirm
                loading={loading}
                onClick={() =>
                  currentStep === 2
                    ? form.submit()
                    : setCurrentStep(currentStep + 1)
                }
                style={{
                  backgroundColor: "rgba(6, 142, 195, 1)",
                  color: "rgba(255, 255, 255, 1)",
                }}
              >
                {intl.formatMessage({ id: "confirm" })}
              </ButtonFormConfirm>
            </div>
          </FooterContainer>
        </div>
      )}

      {isDefaulter && (
        <ConfirmationModalContent
          description={intl.formatMessage({ id: "credential_locked" })}
          confirmationText={"open_exception"}
          cancelText={"close"}
          confirmationFunction={handleDefaulterConfirmation}
          cancelFunction={handleCloseModal}
        />
      )}
    </ModalWithdrawalAndDisassociate>
  );
};

export default PrintCredential;
